import { useState, useMemo } from "react";
import { X, Search } from "lucide-react";
import "./TagFilter.scss";

function TagFilter({ availableTags = [], topTags = [], selectedTagIds = [], onChange }) {
  const [tagInput, setTagInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const selectedTagObjects = useMemo(
    () => selectedTagIds.map((id) => availableTags.find((t) => t.id === id)).filter(Boolean),
    [selectedTagIds, availableTags]
  );

  const suggestions = useMemo(() => {
    if (!tagInput.trim()) return [];
    const q = tagInput.toLowerCase();
    return availableTags.filter(
      (tag) => tag.name.toLowerCase().includes(q) && !selectedTagIds.includes(tag.id)
    );
  }, [availableTags, tagInput, selectedTagIds]);

  const topTagsFiltered = useMemo(
    () =>
      topTags
        .filter((t) => availableTags.some((at) => at.id === t.id) && !selectedTagIds.includes(t.id))
        .slice(0, 10),
    [topTags, availableTags, selectedTagIds]
  );

  const addTag = (tag) => {
    if (selectedTagIds.includes(tag.id)) return;
    onChange([...selectedTagIds, tag.id]);
    setTagInput("");
    setShowSuggestions(false);
  };

  const removeTag = (tagId) => {
    onChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions.length > 0) addTag(suggestions[0]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  if (availableTags.length === 0 && topTags.length === 0) return null;

  return (
    <div className="tag-filter">
      {selectedTagObjects.length > 0 && (
        <div className="tag-filter__active">
          {selectedTagObjects.map((tag) => (
            <span key={tag.id} className="tag-filter__chip">
              {tag.name}
              <button
                type="button"
                className="tag-filter__chip-remove"
                onClick={() => removeTag(tag.id)}
                aria-label={`Odstrániť filter ${tag.name}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
          <button
            type="button"
            className="tag-filter__clear"
            onClick={() => onChange([])}
          >
            <X size={12} /> Zrušiť
          </button>
        </div>
      )}

      <div className="tag-filter__input-wrap">
        <Search size={13} className="tag-filter__search-icon" />
        <input
          className="tag-filter__input"
          value={tagInput}
          onChange={(e) => {
            setTagInput(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => tagInput && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="Filtrovať podľa tagu..."
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="tag-filter__dropdown">
            {suggestions.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className="tag-filter__suggestion"
                onMouseDown={() => addTag(tag)}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {!tagInput && topTagsFiltered.length > 0 && (
        <div className="tag-filter__top">
          <span className="tag-filter__top-label">Populárne:</span>
          {topTagsFiltered.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className="tag-filter__popular"
              onClick={() => addTag(tag)}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default TagFilter;
